import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, Chip, Menu, MenuItem, TableContainer, Table, TableHead,
    TableBody, TableRow, TableCell, Typography, IconButton, Tooltip, Stack
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ProcedureModal from '../procedures/procedureModal'
import EvolutionTimelineModal from '../procedures/evolutions/evolutionTimelineModal';
import EvolutionModal from '../procedures/evolutions/evolutionModal'
import api from "../../../../api/api";
import SuccessMessage from '../../../../components/successMessage';
import ErrorMessage from '../../../../components/errorMessage';
import Loading from '../../../../components/loading';

export default function ProcedureList({ treatment, onProcedureSaved }) {

    const [isLoading, setLoading] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [procedures, setProcedures] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedProcedure, setSelectedProcedure] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isTimelineModalOpen, setTimelineModalOpen] = useState(false);
    const [isEvolutionModalOpen, setEvolutionModalOpen] = useState(false);

    const getProceduresFromTreatment = useCallback((treatment) => {
        setLoading(true);
        api.get(`/treatments/${treatment.treatmentId}/procedures`, {
            headers: {
                Authorization: sessionStorage.getItem('token')
            }
        }).then((returnFromApi) => {
            setProcedures(returnFromApi.data);
            setLoading(false);
        }).catch((returnFromApi) => {
            setLoading(false);
            console.error("ops! ocorreu um erro" + returnFromApi);
            setErrorMessage(returnFromApi.response.data);
        });
    }, []);


    useEffect(() => {
        getProceduresFromTreatment(treatment);

    }, [treatment, getProceduresFromTreatment]);

    const openProcedureModal = () => setIsModalOpen(true);
    const closeProcedureModal = () => setIsModalOpen(false);

    const onSaveSuccess = (message) => {
        setShowSuccessMessage(true);
        setSuccessMessage(message);
        getProceduresFromTreatment(treatment);
        closeProcedureModal();
        onProcedureSaved();
    };

    const openActionsMenu = (event, procedure) => {
        setAnchorEl(event.currentTarget);
        setSelectedProcedure(procedure);
    };

    const closeActionsMenu = () => {
        setAnchorEl(null);
    };

    const openEvolutionModal = () => {
        setEvolutionModalOpen(true);
        setAnchorEl(null);
    };

    const onSaveEvolution = (returnFromApi) => {
        if (returnFromApi.data) {
            setShowSuccessMessage(true);
            setSuccessMessage(returnFromApi.data);
            getProceduresFromTreatment(treatment);
        } else {
            setShowErrorMessage(true)
            setErrorMessage(returnFromApi.response.data);
        }
        setEvolutionModalOpen(false);
    };

    function deleteSelectedProcedure() {
        closeActionsMenu();
        setLoading(true);
        api.delete(`/treatments/procedures/${selectedProcedure.id}`, {
            headers: {
                Authorization: sessionStorage.getItem('token')
            }
        }).then((returnFromApi) => {
            setShowSuccessMessage(true);
            setSuccessMessage(returnFromApi.data);
            getProceduresFromTreatment(treatment);
            onProcedureSaved();
            setLoading(false);
        }).catch((returnFromApi) => {
            setLoading(false);
            console.error("ops! ocorreu um erro" + returnFromApi);
            setErrorMessage(returnFromApi?.response?.data);

        });
    };

    const finishProcedure = () => {
        const procedureId = selectedProcedure.id;
        closeActionsMenu();
        setLoading(true);
        api.patch(`/treatments/procedures/${procedureId}/finish`, null, {
            headers: {
                Authorization: sessionStorage.getItem('token')
            }
        }).then((returnFromApi) => {
            setShowSuccessMessage(true);
            setSuccessMessage(returnFromApi.data);
            getProceduresFromTreatment(treatment);
            onProcedureSaved();
        }).catch((returnFromApi) => {
            setShowErrorMessage(true);
            setErrorMessage(returnFromApi?.response?.data);
            console.error("ops! ocorreu um erro" + returnFromApi);
        }).finally(() => {
            setLoading(false);
        });
    };

    const viewEvolutions = () => {
        if (selectedProcedure) {
            setTimelineModalOpen(true);
        }
        closeActionsMenu();
    };

    if (!treatment) {
        return null;
    }

    return (
        <>
            <Loading isToHidden={isLoading === false} />
            {showSuccessMessage && <SuccessMessage message={successMessage} show={showSuccessMessage} setShow={setShowSuccessMessage} />}
            {showErrorMessage && <ErrorMessage message={errorMessage} show={showErrorMessage} setShow={setShowErrorMessage} />}

            <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Procedimentos</Typography>
                <Tooltip
                    title={treatment?.status === 'Assinado' ? "Não é possível adicionar procedimentos a um tratamento assinado." : ""}
                >
                    <span>
                        <Button
                            variant="outlined"
                            onClick={openProcedureModal}
                            disabled={treatment?.status === 'Assinado'} >
                            Adicionar Procedimento
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            {procedures.length === 0 ? (
                <Typography align="center" sx={{ py: 4 }}>
                    Nenhum procedimento cadastrado ainda.
                </Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {procedures.map((procedure) => (
                                <Tooltip
                                    title={
                                        <div style={{ display: "flex", flexDirection: "column" }}>
                                            <span><strong>Criado em:</strong> {procedure.createdAt}</span>
                                            <span><strong>Finalizado em:</strong> {procedure.finishedAt}</span>
                                        </div>
                                    }>
                                    <TableRow hover key={procedure.id}>
                                        <TableCell sx={{ py: 2 }}>
                                            <Typography variant="body2">{procedure.descricao}</Typography>
                                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                                {procedure.dentePorExtenso && (
                                                    <Tooltip title="Dente">
                                                        <Chip
                                                            label={`D${procedure.dentePorExtenso}`}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    </Tooltip>
                                                )}
                                                {procedure.evolucoes && procedure.evolucoes.length > 0 && (
                                                    <Tooltip title="Quantidade de Evoluções">
                                                        <Chip
                                                            label={`E${procedure.evolucoes.length}`}
                                                            size="small"
                                                            variant="outlined"
                                                            title={`${procedure.evolucoes.length} evoluções registradas`}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Stack>

                                        </TableCell>
                                        <TableCell>{procedure.valor}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={procedure.descricaoDoStatus}
                                                color={
                                                    procedure.status === 'FINALIZADO' ? 'success' :
                                                        procedure.status === 'EM_ANDAMENTO' ? 'warning' : 'info'
                                                }
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 1.5 }}>
                                            <IconButton size="small" onClick={event => openActionsMenu(event, procedure)}>
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                </Tooltip>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeActionsMenu}>
                <MenuItem onClick={viewEvolutions}>Ver Evoluções</MenuItem>
                
                <Tooltip placement="left"
                    title={treatment?.status !== 'Assinado' ? "É necessário assinar o tratamento para adicionar evoluções." : ""}>
                    <span>
                        <MenuItem
                            onClick={openEvolutionModal}
                            disabled={treatment?.status !== 'Assinado'}
                        >
                            Adicionar Evolução
                        </MenuItem>
                    </span>
                </Tooltip>
                
                <Tooltip placement="left"
                    title={treatment?.status !== 'Assinado' ? "É necessário assinar o tratamento para finalizar o procedimento." : ""}>
                    <span>
                        <MenuItem 
                            onClick={finishProcedure} 
                            disabled={selectedProcedure?.status === 'FINALIZADO' || treatment?.status !== 'Assinado'}
                        >
                            Finalizar
                        </MenuItem>
                    </span>
                </Tooltip>

                <Tooltip placement="left"
                    title={selectedProcedure?.status === 'FINALIZADO' ? "Não é possível deletar um procedimento finalizado." : ""}>
                    <span>
                        <MenuItem 
                            onClick={deleteSelectedProcedure} 
                            sx={{ color: 'error.main' }}
                            disabled={selectedProcedure?.status === 'FINALIZADO'}
                        >
                            Deletar
                        </MenuItem>
                    </span>
                </Tooltip>
            </Menu>

            <ProcedureModal isOpen={isModalOpen} onClose={closeProcedureModal} onSaveSuccess={onSaveSuccess} treatment={treatment} />

            <EvolutionTimelineModal open={isTimelineModalOpen} onClose={() => setTimelineModalOpen(false)} procedure={selectedProcedure} />

            <EvolutionModal open={isEvolutionModalOpen} onClose={() => setEvolutionModalOpen(false)} onSaveEvolution={onSaveEvolution} selectedProcedure={selectedProcedure} />
        </>
    );
}